import AuthGuard from "context/auth/AuthGuard"
import { UserRoles } from "constants/userRoles"
import { Box, CircularProgress, Divider, Stack, TextField, styled } from "@mui/material"
import { useTheme } from "@mui/system";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useGetLessons } from "hooks/useGetLessons";
import { useUsersWithBasicTrainingProgresses } from "hooks/useUsersWithBasicTrainingProgresses";
import { UserWithBasicTrainingsInfoModel } from "models/UserWithBasicTrainingsInfoModel";
import { UserBasicInfoTable } from "components/UserBasicInfoTable";
import { LiveDataTable } from "components/views/live/LiveDataTable";
import ConnectButton from "components/ConnectButton/ConnectButton";
import { useParams } from "react-router-dom";
import { StyledMainStack, TitleTypography, StyledAutocomplete, SubtitleTypography } from './LivePage.styles';
import { LessonProgressStatus } from "models/LessonProgressStatus";
import { ConfirmationDialog } from "components/ConfirmationDialog";
import { useStartTrainingForUsers } from "hooks/useStartTrainingForUsers";
import { useCheckUsersLiveTrainingProgresses } from "hooks/useCheckUsersLiveTrainingProgresses";
import { LessonFlat } from "models/LessonFlat";
import { LoadingButton } from "@mui/lab";

const StyledStartTrainingButton = styled(LoadingButton)({
    whiteSpace: "nowrap",
});


export const LivePage = () => {
    const [selectedLessonIds, setSelectedLessonIds] = useState<string[]>([]);
    const [inputValue, setInputValue] = useState<string>('');
    const [users, setUsers] = useState<UserWithBasicTrainingsInfoModel[]>([]);
    const [userRowsSelected, setUserRowsSelected] = useState<number[]>([]);
    const [userIdsToLivePreview, setUserIdsToLivePreview] = useState<string[]>([]);
    const [selectedLiveUserIds, setSelectedLiveUserIds] = useState<string[]>([]);
    const { t } = useTranslation();
    const { lessons, fetchingLessons } = useGetLessons();
    const { getUsersWithBasicTrainingProgresses, getUsersWithBasicTrainingProgressesPending } = useUsersWithBasicTrainingProgresses();
    const { checkUsersLiveTrainingProgresses, checkingUsersLiveTrainingProgresses } = useCheckUsersLiveTrainingProgresses();
    const { startTrainingForUsers, startTrainingForUsersPending } = useStartTrainingForUsers();
    const theme = useTheme();
    const { lessonId } = useParams();
    const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
    const [confirmationDialogMessage, setConfirmationDialogMessage] = useState('');
    const [pendingStartUserIds, setPendingStartUserIds] = useState<string[]>([]);

    useEffect(() => {
        let isMounted = true;

        const fetchUsersWithBasicTrainingProgresses = async () => {
            const resp = await getUsersWithBasicTrainingProgresses(selectedLessonIds);
            if (isMounted && resp?.success && resp.data) {
                setUsers(resp.data);
            }
        }

        if (selectedLessonIds.length > 0) {
            fetchUsersWithBasicTrainingProgresses();
        } else {
            setUsers([]);
            setUserRowsSelected([]);
            setUserIdsToLivePreview([]);
        }

        return () => {
            isMounted = false;
        };
    }, [getUsersWithBasicTrainingProgresses, selectedLessonIds]);

    useEffect(() => {
        if (lessonId) {
            setSelectedLessonIds([lessonId])
        }
    }, [lessonId]);

    const userTableRows = useMemo(() => {
        return users.map((user) => ({
            id: user.userId,
            displayName: user.userDisplayName,
            role: user.userRole,
            tagA: user.userTagA,
            tagB: user.userTagB
        }));
    }, [users]);

    const selectedLessons = useMemo(
        () => lessons.filter((lesson) => selectedLessonIds.includes(lesson.id)),
        [lessons, selectedLessonIds]
    );

    const selectedUserIds = useMemo(
        () => userRowsSelected.map((index) => userTableRows[index]?.id).filter((id): id is string => Boolean(id)),
        [userRowsSelected, userTableRows]
    );

    const shouldShowStartButton = useMemo(
        () => selectedLessonIds.length === 1 && userIdsToLivePreview.length >= 2,
        [selectedLessonIds, userIdsToLivePreview]
    );

    const isStartButtonDisabled = selectedLiveUserIds.length === 0 || checkingUsersLiveTrainingProgresses || startTrainingForUsersPending;

    const handleAutocompleteInputChange = useCallback((_: unknown, newInputValue: string, reason: string) => {
        if (reason !== 'reset') {
            setInputValue(newInputValue);
        }
    }, []);

    const handleLessonChange = useCallback((_: unknown, newValue: unknown) => {
        const selected = Array.isArray(newValue) ? (newValue as LessonFlat[]) : [];
        setSelectedLessonIds(selected.map((lesson) => lesson.id));
    }, []);

    const handleUsersSelected = useCallback((rowsSelected: number[]) => {
        setUserRowsSelected(rowsSelected);
    }, []);

    const handleConnectUsers = useCallback(() => {
        setUserIdsToLivePreview(selectedUserIds);
        setSelectedLiveUserIds([]);
    }, [selectedUserIds]);

    const handleLiveDataDelete = useCallback((ids: string[]) => {
        setUserIdsToLivePreview((prevUserIds) => prevUserIds.filter((id) => !ids.includes(id)));
        setSelectedLiveUserIds((prevSelectedIds) => prevSelectedIds.filter((id) => !ids.includes(id)));
    }, []);

    const handleLiveUsersSelectionChange = useCallback((userIds: string[]) => {
        setSelectedLiveUserIds(userIds);
    }, []);

    const handleStartTraining = useCallback(async (userIds: string[]) => {
        if (selectedLessonIds.length !== 1) {
            return;
        }

        await startTrainingForUsers(selectedLessonIds[0], userIds);
    }, [selectedLessonIds, startTrainingForUsers]);

    const handlePlayClick = useCallback(async () => {
        if (!shouldShowStartButton || selectedLiveUserIds.length === 0) {
            return;
        }

        const liveTrainingInfo = await checkUsersLiveTrainingProgresses({ userIds: selectedLiveUserIds, lessonId: selectedLessonIds[0] });
        setPendingStartUserIds(selectedLiveUserIds);
        const inProgressUsers = liveTrainingInfo.filter((user) => user.status === LessonProgressStatus.InProgress);

        if (inProgressUsers.length > 0) {
            const usersNames = inProgressUsers.map((user) => user.userDisplayName).join(', ');
            setConfirmationDialogMessage(t('live.overwriteProgressMessage', { userNames: usersNames }));
            setConfirmationDialogOpen(true);
            return;
        }

        await handleStartTraining(selectedLiveUserIds);
        setPendingStartUserIds([]);
    }, [
        checkUsersLiveTrainingProgresses,
        handleStartTraining,
        selectedLiveUserIds,
        selectedLessonIds,
        shouldShowStartButton,
        t,
    ]);

    const handleConfirmOverwrite = useCallback(async () => {
        await handleStartTraining(pendingStartUserIds);
        setConfirmationDialogOpen(false);
        setPendingStartUserIds([]);
    }, [handleStartTraining, pendingStartUserIds]);

    const handleCloseConfirmation = useCallback(() => {
        setConfirmationDialogOpen(false);
        setPendingStartUserIds([]);
    }, []);

    return (
        <AuthGuard requiredRoles={[UserRoles.ADMIN, UserRoles.Patron]}>            
            <StyledMainStack>
                <TitleTypography variant="h4" component="div">
                    {t('live.title')}
                </TitleTypography>
                <SubtitleTypography variant="subtitle1">
                    {t('live.subtitle')}
                </SubtitleTypography>
                <Divider />
                <Stack direction={'column'}>
                    <Stack direction={'row'} alignItems={'center'} spacing={1} width={'100%'} paddingTop={theme.spacing(4)}>
                        <Box width='25%' minWidth='22rem' flexShrink={0} paddingRight={theme.spacing(2)}>
                        {fetchingLessons ? (
                            <CircularProgress />
                        ) : (
                            <StyledAutocomplete
                                multiple
                                id="live-autocomplete"
                                options={lessons}
                                getOptionLabel={(option) => (option as LessonFlat).name}
                                value={selectedLessons}
                                inputValue={inputValue}
                                onInputChange={handleAutocompleteInputChange}
                                onChange={handleLessonChange}
                                renderInput={(params) => (
                                    <TextField {...params} variant="outlined" label={t('live.chooseOrFetchLessons')} />
                                )}
                                noOptionsText={t('live.noOptions')}
                            />
                        )}
                        </Box>
                        {shouldShowStartButton && (
                            <StyledStartTrainingButton
                                onClick={handlePlayClick}
                                color={theme.palette.main}
                                variant="contained"
                                loading={checkingUsersLiveTrainingProgresses || startTrainingForUsersPending}
                                disabled={isStartButtonDisabled}
                            >
                                {t('live.startTrainingButton')}
                            </StyledStartTrainingButton>
                        )}
                    </Stack>
                    <Stack direction={'row'} width={'100%'}>
                        <Box width={'100%'} paddingRight={theme.spacing(5)} paddingTop={theme.spacing(5)}>
                           <UserBasicInfoTable
                                title={t('usersManagement.users')}
                                users={userTableRows}
                                fetchingUsers={getUsersWithBasicTrainingProgressesPending}
                                setUsersSelected={handleUsersSelected}
                            />
                        </Box>
                        <ConnectButton
                            onClick={handleConnectUsers}
                            disabled={selectedLessonIds.length === 0 || userRowsSelected.length === 0}
                        />
                        <Box width={'100%'} paddingLeft={theme.spacing(5)} paddingTop={theme.spacing(5)}>
                            <LiveDataTable
                                userIds={userIdsToLivePreview}
                                lessonIds={selectedLessonIds}
                                onDataDelete={handleLiveDataDelete}
                                onUsersSelectionChange={handleLiveUsersSelectionChange}
                            />
                        </Box>
                    </Stack>
                </Stack>
                <ConfirmationDialog
                    open={confirmationDialogOpen}
                    handleConfirm={handleConfirmOverwrite}
                    handleClose={handleCloseConfirmation}
                    message={confirmationDialogMessage}
                    confirmButtonText={t('live.overrideButton')}
                />
            </StyledMainStack>
        </AuthGuard>
    )
}